import React from "react";
import Link from "components/link";
import { PortableText } from "@portabletext/react";
import ButtonLink from "components/button-link";
import CountdownTimer from "components/countdown-timer";
import useTimer from "hooks/useTimer";
import clsx from "clsx/lite";

const ButtonRenderer = ({ value, limitContentWidth }) => (
	<ButtonLink data={value}
		className={clsx(
			"font-black py-2.5 md:py-3 px-2",
			limitContentWidth ? "w-60 xl:min-w-[280px]" : "w-72 xxl:w-80  tracking-wide ",
			value.style === "secondary" && "border border-beige")} />
);

const TimerRenderer = ({ value }) => {
	const { startDate, endDate, showDays } = value;
	const { timer, active = false } = useTimer({ startDate, endDate, showDays });
	return (
		active &&
		<div className="flex justify-start pb-4">
			<CountdownTimer timer={timer} compactView useSmallElements />
		</div>
	);
};

// Marks
const Links = ({ children, value }) => <Link to={value.href} isTargetBlank={value.openInNewTab}>{children}</Link>;

const CenterText = ({ children }) => <div className="text-center">{children}</div>;

const TextStyle = ({ children, value, limitContentWidth }) => {
	const bgColor = value?.bgColor?.color?.hex;
	const flashingColor1 = value?.flashingColor1?.hex;
	const flashingColor2 = value?.flashingColor2?.hex;
	return (
		<span
			className={clsx(
				"text-center w-fit",
				value.flashing && "animated-flash",
				value?.fontFamily === "Prata" && "text-[85%]",
				bgColor && "block py-1 px-4 min-w-60 xl:min-w-[280px]",
				!limitContentWidth && "w-full"
			)}
			style={{
				fontFamily: value?.fontFamily,
				color: value?.highlight?.color?.hex,
				backgroundColor: bgColor,
				"--flashing-color-1": flashingColor1,
				"--flashing-color-2": flashingColor2,
			}}>
			{children}
		</span>
	);
};

const richTextComponents = (limitContentWidth) => ({
	types: {
		button: ({ value }) => <ButtonRenderer value={value} limitContentWidth={limitContentWidth} />,
		timer: TimerRenderer,
	},
	marks: {
		link: Links,
		center: CenterText,
		textStyle: ({ value, children }) => <TextStyle value={value} limitContentWidth={limitContentWidth}>{children}</TextStyle>,
	},
	block: {
		h1: ({ children }) => <h1 className="mx-3 sm:mx-0 text-[40px] md:text-h1">{children}</h1>,
		h2: ({ children }) => <h2 className="text-3xl xs:text-[40px] md:text-h2 xxl:text-h1 leading-[50px]">{children}</h2>,
		h5: ({ children }) => <h5 className="text-base xs:text-xl md:text-h5 xxl:text-h4">{children}</h5>,
		h6: ({ children }) => <h6 className="tracking-widest">{children}</h6>,
		small: ({ children }) => <span className="text-sm">{children}</span>,
		blockquote: ({ children }) => (
			<blockquote
				className={clsx(
					"text-sm tracking-[0.25rem] font-bold font-poppins xl:text-lg",
					limitContentWidth ? "lg:tracking-[0.3rem]" : "")}
			>
				{children}
			</blockquote>
		),
	},
});

const RichText = ({ data, limitContentWidth, className }) => {
	if (!data?.length) return null;
	return (
		<div className={clsx(
			"rich-text",
			className,
			limitContentWidth && "md:w-100", "flex flex-col items-center")}>
			<PortableText value={data} components={richTextComponents(limitContentWidth)} />
		</div>
	);
};

export default RichText;
